import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import AdminSidebar from '../components/AdminSidebar';
import MobileNav from '../components/MobileNav';
import { useNotification } from '../context/NotificationContext';
import { Loader, Menu, Bell, User, Phone } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import NotificationCenter from '../components/NotificationCenter';
import MobileTopMenu from '../components/MobileTopMenu';
import Logo from '../components/Logo';
import { useAdminAuth } from '../context/AdminAuthContext';

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { adminUser, loading: authLoading, signOut } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [adminEmail, setAdminEmail] = useState<string | null>(null);

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session) {
          navigate('/admin/login');
          return;
        }

        // If adminUser is null after auth check, redirect to login
        if (!authLoading && !adminUser) {
          navigate('/admin/login');
          return;
        }

        setAdminEmail(session.user.email);
        setLoading(false);
      } catch (error) {
        console.error('Admin verification error:', error);
        showNotification('error', 'Unauthorized access');
        navigate('/admin/login');
      }
    };

    if (!authLoading) {
      checkAdminAccess();
    }
  }, [navigate, showNotification, authLoading, adminUser]);

  // Close sidebar on route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    try {
      await signOut();
      // Navigation is handled in the signOut function
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const adminMenuItems = [
    { path: '/admin', label: 'Dashboard' },
    { path: '/admin/users', label: 'Users' },
    { path: '/admin/bookings', label: 'Bookings' },
    { path: '/admin/services', label: 'Services' },
    { path: '/admin/coupons', label: 'Coupons' },
    { path: '/admin/time-slots', label: 'Time Slots' },
    { path: '/admin/settings', label: 'Settings' }
  ];

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // If no admin user, don't render the layout
  if (!adminUser) {
    return null;
  }

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <AdminSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen md:ml-72">
        {/* Top Contact Bar */}
        <div className="bg-blue-600 dark:bg-blue-700 text-white py-1.5 text-sm hidden md:block">
          <div className="container mx-auto px-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <a href="tel:240-289-3658" className="flex items-center gap-1 hover:text-blue-100 transition-colors">
                <Phone className="w-4 h-4" />
                <span>240-289-3658</span>
              </a>
            </div>
            <div className="text-sm">
              <span>Admin Portal</span>
            </div>
          </div>
        </div>
        
        {/* Top Bar */}
        <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 sticky top-0 z-50">
          <div className="px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg md:hidden"
              >
                <Menu className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              </button>
              <Logo size="small" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {location.pathname === '/admin' && 'Dashboard'}
                {location.pathname === '/admin/users' && 'Users'}
                {location.pathname === '/admin/bookings' && 'Bookings'}
                {location.pathname === '/admin/services' && 'Services'}
                {location.pathname === '/admin/coupons' && 'Coupons'}
                {location.pathname === '/admin/time-slots' && 'Time Slots'}
                {location.pathname === '/admin/audit-logs' && 'Audit Logs'}
                {location.pathname === '/admin/settings' && 'Settings'}
                {location.pathname === '/admin/analytics' && 'Analytics'}
              </h1>
            </div>

            <div className="flex items-center gap-4">
              {/* Mobile Phone Number */}
              <a href="tel:240-289-3658" className="md:hidden flex items-center gap-1 text-blue-600 dark:text-blue-400">
                <Phone className="w-5 h-5" />
              </a>
              
              {/* Mobile Menu - Moved to right side */}
              <div className="md:hidden">
                <MobileTopMenu menuItems={adminMenuItems} />
              </div>
              <NotificationCenter />
              <ThemeToggle />
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden md:inline">{adminEmail}</span>
                </button>
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-1 z-50">
                    <button
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-8 overflow-x-hidden">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Navigation */}
      <MobileNav isAdmin />
    </div>
  );
}